import type {
  ConfiguratorState,
  EdgeProfile,
  FinishId,
  HandleOpenSide,
  HandleVariant,
  MachineId,
  MaterialId,
  ProductId,
  ValidationIssue,
} from './configurator-types';
import { REFERENCE_CONFIG } from './configurator-reference';
import { OUTPUT_FINGERPRINT_VERSION, PRICING_VERSION } from './configurator-version';

type NumericLimits = { min: number; max: number };

export const DOOR_LIMITS = {
  widthMm: { min: 200, max: 1_600 },
  heightMm: { min: 180, max: 2_400 },
  thicknessMm: { min: 20, max: 100 },
  quantity: { min: 1, max: 100 },
  minimumBackWallMm: 2.5,
} as const;

const PRODUCT_IDS = [
  'door-handle-edge',
  'door-handle-full',
  'door-handle-double',
] as const satisfies readonly ProductId[];
const MACHINE_IDS = ['bdv-5axis', 'bdv-router', 'external'] as const satisfies readonly MachineId[];
const MATERIAL_IDS = [
  'wood',
  'plastic',
  'zinc',
  'aluminium',
] as const satisfies readonly MaterialId[];
const FINISH_IDS = ['natural', 'oil', 'matte', 'technical'] as const satisfies readonly FinishId[];
const OPEN_SIDES = ['left', 'right'] as const satisfies readonly HandleOpenSide[];
const EDGE_PROFILES = ['square', 'chamfer', 'roundover'] as const satisfies readonly EdgeProfile[];

const DOMAIN_FALLBACKS = REFERENCE_CONFIG;

export type DoorFieldLimits = {
  variant: HandleVariant;
  capCount: 0 | 1 | 2;
  widthMm: NumericLimits;
  heightMm: NumericLimits;
  thicknessMm: NumericLimits;
  quantity: NumericLimits;
  minimumBackWallMm: number;
  handle: {
    lengthMm: NumericLimits;
    offsetMm: NumericLimits;
    faceWidthMm: NumericLimits;
    depthMm: NumericLimits;
    transitionLengthMm: NumericLimits;
    transitionRoundness: NumericLimits;
    grooveWidthMm: NumericLimits;
    roughingAllowanceMm: NumericLimits;
  };
  edgeTreatment: {
    sizeMm: NumericLimits;
    cornerRadiusMm: NumericLimits;
  };
  stockAllowanceMm: {
    widthMm: NumericLimits;
    heightMm: NumericLimits;
    thicknessMm: NumericLimits;
  };
};

export type DerivedDoorGeometry = {
  variant: HandleVariant;
  finished: { widthMm: number; heightMm: number; thicknessMm: number };
  stock: { widthMm: number; heightMm: number; thicknessMm: number };
  handle: {
    startMm: number;
    endMm: number;
    lengthMm: number;
    offsetMm: number;
    faceWidthMm: number;
    depthMm: number;
    remainingWallMm: number;
    transitionLengthMm: number;
    transitionRoundness: number;
    grooveWidthMm: number;
    openLeft: boolean;
    openRight: boolean;
    capLeft: boolean;
    capRight: boolean;
  };
  edge: {
    profile: ConfiguratorState['edgeTreatment']['profile'];
    sizeMm: number;
    cornerRadiusMm: number;
  };
};

export type HandleTransitionPoint = Readonly<{ x: number; y: number }>;

export type HandleTransitionCurve = Readonly<{
  start: HandleTransitionPoint;
  startControl: HandleTransitionPoint;
  startCurvatureControl: HandleTransitionPoint;
  endCurvatureControl: HandleTransitionPoint;
  endControl: HandleTransitionPoint;
  end: HandleTransitionPoint;
}>;

/**
 * Describes one uninterrupted quintic transition between the panel edge and
 * the recessed handle track. The first three and final three controls are
 * evenly spaced on their adjacent horizontals. The transition therefore joins
 * both straight sections with matching tangent and zero endpoint curvature.
 */
export function buildHandleTransitionCurve(
  start: HandleTransitionPoint,
  end: HandleTransitionPoint,
  roundnessPercent: number,
): HandleTransitionCurve {
  const roundness = clamp(roundnessPercent, 0, 100) / 100;
  const controlRatio = 0.125 + roundness * 0.125;
  const deltaX = end.x - start.x;
  const controlOffset = deltaX * controlRatio;

  return {
    start: { ...start },
    startControl: {
      x: start.x + controlOffset,
      y: start.y,
    },
    startCurvatureControl: {
      x: start.x + controlOffset * 2,
      y: start.y,
    },
    endCurvatureControl: {
      x: end.x - controlOffset * 2,
      y: end.y,
    },
    endControl: {
      x: end.x - controlOffset,
      y: end.y,
    },
    end: { ...end },
  };
}

export function evaluateHandleTransition(
  curve: HandleTransitionCurve,
  progress: number,
): HandleTransitionPoint {
  const t = clamp(finiteNumber(progress, 0), 0, 1);
  const inverse = 1 - t;
  const weights = [
    inverse ** 5,
    5 * inverse ** 4 * t,
    10 * inverse ** 3 * t ** 2,
    10 * inverse ** 2 * t ** 3,
    5 * inverse * t ** 4,
    t ** 5,
  ];
  const controls = [
    curve.start,
    curve.startControl,
    curve.startCurvatureControl,
    curve.endCurvatureControl,
    curve.endControl,
    curve.end,
  ];

  return controls.reduce(
    (point, control, index) => ({
      x: point.x + control.x * weights[index],
      y: point.y + control.y * weights[index],
    }),
    { x: 0, y: 0 },
  );
}

export function evaluateHandleTransitionTangent(
  curve: HandleTransitionCurve,
  progress: number,
): HandleTransitionPoint {
  const t = clamp(finiteNumber(progress, 0), 0, 1);
  const inverse = 1 - t;
  const weights = [
    inverse ** 4,
    4 * inverse ** 3 * t,
    6 * inverse ** 2 * t ** 2,
    4 * inverse * t ** 3,
    t ** 4,
  ];
  const controls = [
    curve.start,
    curve.startControl,
    curve.startCurvatureControl,
    curve.endCurvatureControl,
    curve.endControl,
    curve.end,
  ];

  return weights.reduce(
    (tangent, weight, index) => ({
      x: tangent.x + 5 * weight * (controls[index + 1].x - controls[index].x),
      y: tangent.y + 5 * weight * (controls[index + 1].y - controls[index].y),
    }),
    { x: 0, y: 0 },
  );
}

/** Samples the shared C2 curve for line-only outputs such as SVG and PDF. */
export function sampleHandleTransition(
  curve: HandleTransitionCurve,
  segmentCount = 40,
): HandleTransitionPoint[] {
  const segments = Math.max(1, Math.floor(finiteNumber(segmentCount, 40)));
  const points: HandleTransitionPoint[] = [];

  for (let index = 0; index <= segments; index += 1) {
    points.push(evaluateHandleTransition(curve, index / segments));
  }

  return points;
}

export function getHandleVariant(productId: ProductId): HandleVariant {
  const safeProductId = allowlisted(productId, PRODUCT_IDS, DOMAIN_FALLBACKS.productId);
  if (safeProductId === 'door-handle-full') return 'full-edge';
  if (safeProductId === 'door-handle-double') return 'double-terminated';
  return 'edge-terminated';
}

/**
 * Product selection starts a fresh geometry preset for that variant. Without
 * this boundary, values forced by one variant (for example a 426 mm full-edge
 * run) leak into the next one and can move the reference transition to X=0.
 */
export function getDoorProductPreset(
  config: ConfiguratorState,
  productId: ProductId,
): Pick<ConfiguratorState, 'productId' | 'handle'> {
  const handle: ConfiguratorState['handle'] = { ...config.handle };
  if (productId === 'door-handle-full') {
    handle.lengthMm = config.widthMm;
    handle.transitionLengthMm = 0;
  } else {
    handle.lengthMm = REFERENCE_CONFIG.handle.lengthMm;
    handle.transitionLengthMm = REFERENCE_CONFIG.handle.transitionLengthMm;
    if (productId === 'door-handle-edge') {
      handle.openSide = REFERENCE_CONFIG.handle.openSide;
    } else {
      handle.offsetMm = REFERENCE_CONFIG.handle.offsetMm;
    }
  }

  const canonical = normalizeDoorConfig({
    ...config,
    productId,
    handle,
  });

  return {
    productId: canonical.productId,
    handle: canonical.handle,
  };
}

/**
 * The single source of numeric domain limits. Dependent ranges are calculated in
 * manufacturing order: panel -> handle length -> offset -> transition -> depth.
 */
export function getDoorFieldLimits(config: ConfiguratorState): DoorFieldLimits {
  const source = asRecord(config);
  const handle = asRecord(source.handle);
  const productId = allowlisted(source.productId, PRODUCT_IDS, DOMAIN_FALLBACKS.productId);
  const variant = getHandleVariant(productId);
  const widthMm = clampWithFallback(source.widthMm, DOOR_LIMITS.widthMm, DOMAIN_FALLBACKS.widthMm);
  const heightMm = clampWithFallback(
    source.heightMm,
    DOOR_LIMITS.heightMm,
    DOMAIN_FALLBACKS.heightMm,
  );
  const thicknessMm = clampWithFallback(
    source.thicknessMm,
    DOOR_LIMITS.thicknessMm,
    DOMAIN_FALLBACKS.thicknessMm,
  );

  const lengthMm =
    variant === 'full-edge'
      ? { min: widthMm, max: widthMm }
      : variant === 'double-terminated'
        ? { min: 100, max: Math.max(100, widthMm - 20) }
        : { min: 100, max: widthMm };
  const canonicalLengthMm = clampWithFallback(
    handle.lengthMm,
    lengthMm,
    variant === 'full-edge' ? widthMm : DOMAIN_FALLBACKS.handle.lengthMm,
  );

  const offsetMm =
    variant === 'double-terminated'
      ? {
          min: 10,
          max: Math.max(10, widthMm - canonicalLengthMm - 10),
        }
      : {
          // Offset is dormant for the edge/full variants, but preserving it keeps
          // switching variants backward-compatible with existing drafts.
          min: 0,
          max: Math.max(0, widthMm - 60),
        };
  const capCount: 0 | 1 | 2 =
    variant === 'double-terminated' ? 2 : variant === 'edge-terminated' ? 1 : 0;
  const transitionLengthMm =
    capCount === 0
      ? { min: 0, max: 0 }
      : {
          min: 20,
          max: Math.max(20, Math.min(180, (canonicalLengthMm - 20) / capCount)),
        };
  const depthMm = {
    min: 2,
    max: Math.max(2, thicknessMm - DOOR_LIMITS.minimumBackWallMm),
  };
  const canonicalDepthMm = clampWithFallback(
    handle.depthMm,
    depthMm,
    DOMAIN_FALLBACKS.handle.depthMm,
  );

  return {
    variant,
    capCount,
    widthMm: { ...DOOR_LIMITS.widthMm },
    heightMm: { ...DOOR_LIMITS.heightMm },
    thicknessMm: { ...DOOR_LIMITS.thicknessMm },
    quantity: { ...DOOR_LIMITS.quantity },
    minimumBackWallMm: DOOR_LIMITS.minimumBackWallMm,
    handle: {
      lengthMm,
      offsetMm,
      faceWidthMm: {
        min: 12,
        max: Math.max(12, Math.min(120, Math.round(heightMm * 0.4))),
      },
      depthMm,
      transitionLengthMm,
      transitionRoundness: { min: 0, max: 100 },
      grooveWidthMm: { min: 4, max: 40 },
      roughingAllowanceMm: {
        min: 0.1,
        max: Math.max(0.1, Math.min(3, canonicalDepthMm - 0.1)),
      },
    },
    edgeTreatment: {
      sizeMm: {
        min: 0.5,
        max: Math.max(0.5, thicknessMm / 2 - 0.5),
      },
      cornerRadiusMm: {
        min: 0,
        max: Math.min(30, Math.min(widthMm, heightMm) / 2),
      },
    },
    stockAllowanceMm: {
      widthMm: { min: 0, max: 30 },
      heightMm: { min: 0, max: 30 },
      thicknessMm: { min: 0, max: 20 },
    },
  };
}

export function normalizeDoorConfig(config: ConfiguratorState): ConfiguratorState {
  const source = { ...asRecord(config) };
  // Older saved configurations may still contain the retired DXF request flag.
  // Accept the object, but do not let the legacy field leak back into canonical state.
  delete source.includeDxf;
  const handle = asRecord(source.handle);
  const edgeTreatment = asRecord(source.edgeTreatment);
  const stockAllowanceMm = asRecord(source.stockAllowanceMm);

  const productId = allowlisted(source.productId, PRODUCT_IDS, DOMAIN_FALLBACKS.productId);
  const machineId = allowlisted(source.machineId, MACHINE_IDS, DOMAIN_FALLBACKS.machineId);
  const materialId = allowlisted(source.materialId, MATERIAL_IDS, DOMAIN_FALLBACKS.materialId);
  const finishId = allowlisted(
    source.finishId,
    FINISH_IDS,
    materialId === 'wood' ? DOMAIN_FALLBACKS.finishId : 'technical',
  );
  const openSide = allowlisted(handle.openSide, OPEN_SIDES, DOMAIN_FALLBACKS.handle.openSide);
  const edgeProfile = allowlisted(
    edgeTreatment.profile,
    EDGE_PROFILES,
    DOMAIN_FALLBACKS.edgeTreatment.profile,
  );
  const limits = getDoorFieldLimits({
    ...source,
    productId,
    machineId,
    materialId,
    finishId,
    handle: { ...handle, openSide },
    edgeTreatment: { ...edgeTreatment, profile: edgeProfile },
  } as ConfiguratorState);

  // Keep this sequence explicit: later fields have limits derived from earlier ones.
  const widthMm = clampWithFallback(source.widthMm, limits.widthMm, DOMAIN_FALLBACKS.widthMm);
  const heightMm = clampWithFallback(source.heightMm, limits.heightMm, DOMAIN_FALLBACKS.heightMm);
  const thicknessMm = clampWithFallback(
    source.thicknessMm,
    limits.thicknessMm,
    DOMAIN_FALLBACKS.thicknessMm,
  );
  const lengthMm = clampWithFallback(
    handle.lengthMm,
    limits.handle.lengthMm,
    limits.variant === 'full-edge' ? widthMm : DOMAIN_FALLBACKS.handle.lengthMm,
  );
  const offsetMm = clampWithFallback(
    handle.offsetMm,
    limits.handle.offsetMm,
    DOMAIN_FALLBACKS.handle.offsetMm,
  );
  const transitionLengthMm = clampWithFallback(
    handle.transitionLengthMm,
    limits.handle.transitionLengthMm,
    limits.capCount === 0 ? 0 : DOMAIN_FALLBACKS.handle.transitionLengthMm,
  );
  const faceWidthMm = clampWithFallback(
    handle.faceWidthMm,
    limits.handle.faceWidthMm,
    DOMAIN_FALLBACKS.handle.faceWidthMm,
  );
  const depthMm = clampWithFallback(
    handle.depthMm,
    limits.handle.depthMm,
    DOMAIN_FALLBACKS.handle.depthMm,
  );
  const grooveWidthMm = clampWithFallback(
    handle.grooveWidthMm,
    limits.handle.grooveWidthMm,
    DOMAIN_FALLBACKS.handle.grooveWidthMm,
  );
  const roughingAllowanceMm = clampWithFallback(
    handle.roughingAllowanceMm,
    limits.handle.roughingAllowanceMm,
    DOMAIN_FALLBACKS.handle.roughingAllowanceMm,
  );

  return {
    ...(source as unknown as ConfiguratorState),
    productId,
    machineId,
    materialId,
    widthMm,
    heightMm,
    thicknessMm,
    quantity: clamp(
      Math.round(finiteNumber(source.quantity, DOMAIN_FALLBACKS.quantity)),
      limits.quantity.min,
      limits.quantity.max,
    ),
    handle: {
      openSide,
      lengthMm,
      offsetMm,
      faceWidthMm,
      depthMm,
      transitionLengthMm,
      transitionRoundness: clampWithFallback(
        handle.transitionRoundness,
        limits.handle.transitionRoundness,
        DOMAIN_FALLBACKS.handle.transitionRoundness,
      ),
      grooveWidthMm,
      roughingAllowanceMm,
    },
    edgeTreatment: {
      profile: edgeProfile,
      sizeMm: clampWithFallback(
        edgeTreatment.sizeMm,
        limits.edgeTreatment.sizeMm,
        DOMAIN_FALLBACKS.edgeTreatment.sizeMm,
      ),
      cornerRadiusMm: clampWithFallback(
        edgeTreatment.cornerRadiusMm,
        limits.edgeTreatment.cornerRadiusMm,
        DOMAIN_FALLBACKS.edgeTreatment.cornerRadiusMm,
      ),
    },
    stockAllowanceMm: {
      widthMm: clampWithFallback(
        stockAllowanceMm.widthMm,
        limits.stockAllowanceMm.widthMm,
        DOMAIN_FALLBACKS.stockAllowanceMm.widthMm,
      ),
      heightMm: clampWithFallback(
        stockAllowanceMm.heightMm,
        limits.stockAllowanceMm.heightMm,
        DOMAIN_FALLBACKS.stockAllowanceMm.heightMm,
      ),
      thicknessMm: clampWithFallback(
        stockAllowanceMm.thicknessMm,
        limits.stockAllowanceMm.thicknessMm,
        DOMAIN_FALLBACKS.stockAllowanceMm.thicknessMm,
      ),
    },
    finishId,
    includeQuote: booleanValue(source.includeQuote, true),
    includeDrawing: booleanValue(source.includeDrawing, true),
    includeConfigFile: booleanValue(source.includeConfigFile, true),
    includeTcnFile: booleanValue(source.includeTcnFile, true),
    customerName: stringValue(source.customerName),
    customerEmail: stringValue(source.customerEmail),
    company: stringValue(source.company),
    phone: stringValue(source.phone),
    note: stringValue(source.note),
    consent: booleanValue(source.consent, false),
  };
}

/**
 * Geometry consumes canonical values only. The public boundary normalizes legacy
 * callers, while the actual derivation below performs no secondary clamping.
 */
export function deriveDoorGeometry(config: ConfiguratorState): DerivedDoorGeometry {
  const canonical = normalizeDoorConfig(config);
  const variant = getHandleVariant(canonical.productId);
  const widthMm = canonical.widthMm;
  const lengthMm = canonical.handle.lengthMm;
  let startMm = 0;
  let endMm = lengthMm;
  let openLeft = false;
  let openRight = false;
  let capLeft = false;
  let capRight = false;

  if (variant === 'edge-terminated') {
    if (canonical.handle.openSide === 'left') {
      openLeft = true;
      capRight = true;
    } else {
      startMm = widthMm - lengthMm;
      endMm = widthMm;
      capLeft = true;
      openRight = true;
    }
  } else if (variant === 'double-terminated') {
    startMm = canonical.handle.offsetMm;
    endMm = startMm + lengthMm;
    capLeft = true;
    capRight = true;
  } else {
    endMm = widthMm;
    openLeft = true;
    openRight = true;
  }

  return {
    variant,
    finished: {
      widthMm,
      heightMm: canonical.heightMm,
      thicknessMm: canonical.thicknessMm,
    },
    stock: {
      widthMm: widthMm + canonical.stockAllowanceMm.widthMm,
      heightMm: canonical.heightMm + canonical.stockAllowanceMm.heightMm,
      thicknessMm: canonical.thicknessMm + canonical.stockAllowanceMm.thicknessMm,
    },
    handle: {
      startMm,
      endMm,
      lengthMm: endMm - startMm,
      offsetMm: startMm,
      faceWidthMm: canonical.handle.faceWidthMm,
      depthMm: canonical.handle.depthMm,
      remainingWallMm: canonical.thicknessMm - canonical.handle.depthMm,
      transitionLengthMm: canonical.handle.transitionLengthMm,
      transitionRoundness: canonical.handle.transitionRoundness,
      grooveWidthMm: canonical.handle.grooveWidthMm,
      openLeft,
      openRight,
      capLeft,
      capRight,
    },
    edge: {
      profile: canonical.edgeTreatment.profile,
      sizeMm: canonical.edgeTreatment.sizeMm,
      cornerRadiusMm: canonical.edgeTreatment.cornerRadiusMm,
    },
  };
}

export function validateDoorConfig(config: ConfiguratorState): ValidationIssue[] {
  const source = asRecord(config);
  const handle = asRecord(source.handle);
  const edgeTreatment = asRecord(source.edgeTreatment);
  const stockAllowance = asRecord(source.stockAllowanceMm);
  const limits = getDoorFieldLimits(config);
  const canonical = normalizeDoorConfig(config);
  const issues: ValidationIssue[] = [];

  validateEnum(issues, source.productId, PRODUCT_IDS, 'invalid-product', 'Neplatný typ výrobku.');
  validateEnum(issues, source.machineId, MACHINE_IDS, 'invalid-machine', 'Neplatný výrobný stroj.');
  validateEnum(issues, source.materialId, MATERIAL_IDS, 'invalid-material', 'Neplatný materiál.');
  validateEnum(issues, source.finishId, FINISH_IDS, 'invalid-finish', 'Neplatná povrchová úprava.');
  validateEnum(
    issues,
    handle.openSide,
    OPEN_SIDES,
    'invalid-open-side',
    'Neplatná otvorená strana úchytu.',
  );
  validateEnum(
    issues,
    edgeTreatment.profile,
    EDGE_PROFILES,
    'invalid-edge-profile',
    'Neplatný profil hrany.',
  );

  if (!withinLimits(source.widthMm, limits.widthMm)) {
    issues.push({
      level: 'error',
      code: 'width-out-of-range',
      message: `Šírka musí byť ${limits.widthMm.min}–${limits.widthMm.max} mm.`,
    });
  }
  if (!withinLimits(source.heightMm, limits.heightMm)) {
    issues.push({
      level: 'error',
      code: 'height-out-of-range',
      message: `Výška musí byť ${limits.heightMm.min}–${limits.heightMm.max} mm.`,
    });
  }
  if (!withinLimits(source.thicknessMm, limits.thicknessMm)) {
    issues.push({
      level: 'error',
      code: 'thickness-out-of-range',
      message: `Hrúbka musí byť ${limits.thicknessMm.min}–${limits.thicknessMm.max} mm.`,
    });
  }
  if (
    !isFiniteNumber(source.quantity) ||
    !Number.isInteger(source.quantity) ||
    !withinLimits(source.quantity, limits.quantity)
  ) {
    issues.push({
      level: 'error',
      code: 'quantity-out-of-range',
      message: `Počet kusov musí byť ${limits.quantity.min}–${limits.quantity.max}.`,
    });
  }
  if (!withinLimits(handle.lengthMm, limits.handle.lengthMm)) {
    issues.push({
      level: 'error',
      code: 'handle-length-out-of-range',
      message: 'Dĺžka úchytu je mimo rozsahu pre zvolený variant a šírku dvierok.',
    });
  }
  if (
    limits.variant === 'double-terminated' &&
    !withinLimits(handle.offsetMm, limits.handle.offsetMm)
  ) {
    issues.push({
      level: 'error',
      code: 'handle-offset-out-of-range',
      message: 'Odsadenie úchytu nezachováva bezpečný okraj na oboch stranách.',
    });
  }
  if (
    limits.capCount > 0 &&
    !withinLimits(handle.transitionLengthMm, limits.handle.transitionLengthMm)
  ) {
    issues.push({
      level: 'error',
      code: 'handle-too-short',
      message: 'Úchyt je príliš krátky pre zvolenú dĺžku plynulého ukončenia.',
    });
  }
  if (!withinLimits(handle.faceWidthMm, limits.handle.faceWidthMm)) {
    issues.push({
      level: 'error',
      code: 'handle-face-width',
      message: 'Výška úchytového pásu je mimo bezpečného rozsahu pre zvolenú výšku dvierok.',
    });
  }
  if (!withinLimits(handle.depthMm, limits.handle.depthMm)) {
    issues.push({
      level: 'error',
      code:
        isFiniteNumber(handle.depthMm) && handle.depthMm > limits.handle.depthMm.max
          ? 'back-wall-too-thin'
          : 'handle-depth-out-of-range',
      message:
        isFiniteNumber(handle.depthMm) && handle.depthMm > limits.handle.depthMm.max
          ? `Za úchytkou musí zostať aspoň ${formatMm(limits.minimumBackWallMm)} mm materiálu.`
          : 'Hĺbka frézovania je mimo povoleného rozsahu.',
    });
  }
  if (!withinLimits(handle.grooveWidthMm, limits.handle.grooveWidthMm)) {
    issues.push({
      level: 'error',
      code: 'groove-width-out-of-range',
      message: 'Návrhová šírka profilu je mimo povoleného rozsahu.',
    });
  }
  if (!withinLimits(handle.transitionRoundness, limits.handle.transitionRoundness)) {
    issues.push({
      level: 'error',
      code: 'transition-roundness-out-of-range',
      message: 'Plynulosť S-krivky musí byť v rozsahu 0–100 %.',
    });
  }
  if (!withinLimits(handle.roughingAllowanceMm, limits.handle.roughingAllowanceMm)) {
    issues.push({
      level: 'error',
      code: 'invalid-roughing-allowance',
      message: 'Prídavok dokončovacieho prechodu musí byť väčší ako 0 a menší než hĺbka.',
    });
  }
  if (!withinLimits(edgeTreatment.sizeMm, limits.edgeTreatment.sizeMm)) {
    issues.push({
      level: 'error',
      code: 'edge-profile-too-large',
      message: 'Veľkosť opracovania hrany je príliš veľká pre zvolenú hrúbku.',
    });
  }
  if (!withinLimits(edgeTreatment.cornerRadiusMm, limits.edgeTreatment.cornerRadiusMm)) {
    issues.push({
      level: 'error',
      code: 'corner-radius-too-large',
      message: 'Rádius rohov je väčší než rozmery dvierok dovoľujú.',
    });
  }
  if (
    !withinLimits(stockAllowance.widthMm, limits.stockAllowanceMm.widthMm) ||
    !withinLimits(stockAllowance.heightMm, limits.stockAllowanceMm.heightMm) ||
    !withinLimits(stockAllowance.thicknessMm, limits.stockAllowanceMm.thicknessMm)
  ) {
    issues.push({
      level: 'error',
      code: 'stock-allowance-out-of-range',
      message: 'Prídavok polotovaru je mimo povoleného rozsahu.',
    });
  }

  if (limits.variant !== 'edge-terminated' || canonical.handle.openSide !== 'right') {
    issues.push({
      level: 'warning',
      code: 'tcn-parametric-program-review',
      message:
        'Tento variant používa parametrickú TCN dráhu; pred výrobou vyžaduje strojovú simuláciu a schválenie technológom.',
    });
  }
  issues.push({
    level: 'warning',
    code: 'tool-profile-unmapped',
    message:
      'Dráha používa návrhovú šírku ako posun osi; výsledný prierez určuje nástroj 1009 a treba ho potvrdiť z nástrojovej tabuľky.',
  });
  if (canonical.edgeTreatment.profile !== 'square') {
    issues.push({
      level: 'warning',
      code: 'edge-operation-unmapped',
      message: 'Round-over/skosenie je v náhľade, ale dodaný TCN nemá potvrdenú operáciu hrán.',
    });
  }
  if (
    canonical.stockAllowanceMm.widthMm !== 3 ||
    canonical.stockAllowanceMm.heightMm !== 0 ||
    canonical.stockAllowanceMm.thicknessMm !== 0
  ) {
    issues.push({
      level: 'warning',
      code: 'stock-allowance-changed',
      message:
        'Zmenený prídavok je zapísaný do rozmerov TCN; jeho rozdelenie voči dátumu a účinok externého makra musí potvrdiť technológ.',
    });
  }

  return issues;
}

/** A stable, non-cryptographic identity of every output-affecting field. */
export function buildConfigurationFingerprint(config: ConfiguratorState) {
  const canonical = normalizeDoorConfig(config);
  const serialized = JSON.stringify([
    OUTPUT_FINGERPRINT_VERSION,
    PRICING_VERSION,
    canonical.productId,
    canonical.machineId,
    canonical.materialId,
    canonical.finishId,
    canonical.widthMm,
    canonical.heightMm,
    canonical.thicknessMm,
    canonical.quantity,
    canonical.handle.openSide,
    canonical.handle.lengthMm,
    canonical.handle.offsetMm,
    canonical.handle.faceWidthMm,
    canonical.handle.depthMm,
    canonical.handle.transitionLengthMm,
    canonical.handle.transitionRoundness,
    canonical.handle.grooveWidthMm,
    canonical.handle.roughingAllowanceMm,
    canonical.edgeTreatment.profile,
    canonical.edgeTreatment.sizeMm,
    canonical.edgeTreatment.cornerRadiusMm,
    canonical.stockAllowanceMm.widthMm,
    canonical.stockAllowanceMm.heightMm,
    canonical.stockAllowanceMm.thicknessMm,
  ]);

  return `${OUTPUT_FINGERPRINT_VERSION}-${hashString(serialized)}`;
}

function validateEnum<T extends string>(
  issues: ValidationIssue[],
  value: unknown,
  allowed: readonly T[],
  code: string,
  message: string,
) {
  if (!allowed.includes(value as T)) {
    issues.push({ level: 'error', code, message });
  }
}

function allowlisted<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function booleanValue(value: unknown, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback;
}

function stringValue(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function finiteNumber(value: unknown, fallback: number) {
  return isFiniteNumber(value) ? value : fallback;
}

function withinLimits(value: unknown, limits: NumericLimits) {
  return isFiniteNumber(value) && value >= limits.min && value <= limits.max;
}

function clampWithFallback(value: unknown, limits: NumericLimits, fallback: number) {
  return clamp(finiteNumber(value, fallback), limits.min, limits.max);
}

function formatMm(value: number) {
  return new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 1 }).format(value);
}

export function clamp(value: number, min: number, max: number) {
  const safeMin = finiteNumber(min, 0);
  const safeMax = Math.max(safeMin, finiteNumber(max, safeMin));
  return Math.min(safeMax, Math.max(safeMin, finiteNumber(value, safeMin)));
}

function hashString(value: string) {
  let first = 0xdeadbeef;
  let second = 0x41c6ce57;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    first = Math.imul(first ^ code, 2_654_435_761);
    second = Math.imul(second ^ code, 1_597_334_677);
  }
  first =
    Math.imul(first ^ (first >>> 16), 2_246_822_507) ^
    Math.imul(second ^ (second >>> 13), 3_266_489_909);
  second =
    Math.imul(second ^ (second >>> 16), 2_246_822_507) ^
    Math.imul(first ^ (first >>> 13), 3_266_489_909);
  return `${(second >>> 0).toString(16).padStart(8, '0')}${(first >>> 0)
    .toString(16)
    .padStart(8, '0')}`;
}
